/**
 * 权威飞行模式表。
 *
 * ## 数据来源与"不要手抄"
 *
 * 模式号**全部**来自 `@hgc/mavlink` 的生成产物 —— 也就是仓库里那份固定提交的
 * `packages/mavlink/definitions/v1.0/ardupilotmega.xml`。上游的
 * `COPTER_MODE` / `PLANE_MODE` / `ROVER_MODE` / `SUB_MODE` / `TRACKER_MODE`
 * 枚举就是 ArduPilot 自己声明的编号，本文件把它们整理成双向查表。
 *
 * 这样做的直接收益：上游增删模式（ArduPilot 加模式很频繁）之后，
 * 只要 `npm run gen:mavlink` 跑一遍，这里的 `modeName` / `modeNumber`
 * 自动跟上；如果照抄一份数字表，就会慢慢腐化成"看起来对、实际缺一半"。
 *
 * **飞艇是个例外**：当前快照里没有 `BLIMP_MODE`（`MAV_TYPE_AIRSHIP` 存在，
 * 但没有对应的模式枚举），所以 blimp 的模式表是手写的，
 * 依据是 ArduBlimp 的 C++ 源码（见 {@link BLIMP_MODE_SOURCE}）。
 * 一旦上游补上 `BLIMP_MODE`，应该把这个手写表换成枚举驱动。
 *
 * ## 模式门禁的诚实原则
 *
 * {@link canSwitchMode} 只编码**有据可依**的拒绝规则，其余一律放行。
 * 理由：瞎猜一条"未解锁不能进 AUTO"这种规则，会让本来能用的操作被界面拦下，
 * 而用户没有任何办法绕过；相比之下，"少拦一次"最多是让飞控自己回一个
 * `MAV_RESULT_DENIED`。所以不确定时的默认答案是"允许"，并把依据写清楚。
 */

import { enumByName } from '@hgc/mavlink';
import type { VehicleKind } from './vehicles';
import { VEHICLE_KINDS } from './vehicles';

/** blimp 模式表的手写依据。 */
export const BLIMP_MODE_SOURCE =
  'ArduBlimp 的 `Blimp/mode.h::Mode::Number`（LAND=0 / MANUAL=1 / VELOCITY=2 / LOITER=3 / ' +
  'RTL=4 / AUTO=5 / HOLD=6，另保留 30 给 offboard 扩展）；' +
  '核对日期 2026-09-25，来源 https://raw.githubusercontent.com/ArduPilot/ardupilot/master/Blimp/mode.h';

/** 每个类别对应的上游模式枚举名。blimp 为 `undefined`（快照里没有 BLIMP_MODE）。 */
const MODE_ENUM_BY_KIND: Readonly<Record<VehicleKind, string | undefined>> = {
  copter: 'COPTER_MODE',
  plane: 'PLANE_MODE',
  rover: 'ROVER_MODE',
  sub: 'SUB_MODE',
  tracker: 'TRACKER_MODE',
  blimp: undefined,
  unknown: undefined,
};

/**
 * 飞艇模式表（手写，依据见 {@link BLIMP_MODE_SOURCE}）。
 *
 * 用 `[短名, 编号]` 的元组数组而不是对象：`Record<number, string>` 里的数字键
 * 会被 JS 按数字大小重排，而这里的顺序是按源码里的书写顺序来的，
 * 保留原始顺序便于跟 C++ 逐行对照。
 */
const BLIMP_MODES: readonly (readonly [string, number])[] = [
  ['LAND', 0],
  ['MANUAL', 1],
  ['VELOCITY', 2],
  ['LOITER', 3],
  ['RTL', 4],
  ['AUTO', 5],
  ['HOLD', 6],
];

/** 中文模式名。飞手习惯优先于字面直译（LOITER → 悬停，而不是"徘徊"）。 */
const MODE_LABELS: Readonly<Record<Exclude<VehicleKind, 'unknown'>, Readonly<Record<string, string>>>> = {
  copter: {
    STABILIZE: '自稳',
    ACRO: '特技',
    ALT_HOLD: '定高',
    AUTO: '自动航线',
    GUIDED: '引导',
    LOITER: '悬停',
    RTL: '返航',
    CIRCLE: '绕圈',
    LAND: '降落',
    DRIFT: '漂移',
    SPORT: '运动',
    FLIP: '翻滚',
    AUTOTUNE: '自动调参',
    POSHOLD: '定点',
    BRAKE: '刹车',
    THROW: '抛飞',
    AVOID_ADSB: 'ADS-B 避让',
    GUIDED_NOGPS: '无 GPS 引导',
    SMART_RTL: '智能返航',
    FLOWHOLD: '光流定点',
    FOLLOW: '跟随',
    ZIGZAG: 'Z 字飞行',
    SYSTEMID: '系统辨识',
    AUTOROTATE: '自旋降落',
    AUTO_RTL: '自动返航降落',
    TURTLE: '翻正',
  },
  plane: {
    MANUAL: '手动',
    CIRCLE: '绕圈',
    STABILIZE: '自稳',
    TRAINING: '训练',
    ACRO: '特技',
    FLY_BY_WIRE_A: '增稳 A',
    FLY_BY_WIRE_B: '增稳 B',
    CRUISE: '巡航',
    AUTOTUNE: '自动调参',
    AUTO: '自动航线',
    RTL: '返航',
    LOITER: '盘旋',
    TAKEOFF: '起飞',
    AVOID_ADSB: 'ADS-B 避让',
    GUIDED: '引导',
    INITIALIZING: '初始化中',
    QSTABILIZE: '垂直起降·自稳',
    QHOVER: '垂直起降·定高',
    QLOITER: '垂直起降·悬停',
    QLAND: '垂直起降·降落',
    QRTL: '垂直起降·返航',
    QAUTOTUNE: '垂直起降·自动调参',
    QACRO: '垂直起降·特技',
    THERMAL: '热气流滑翔',
    LOITER_ALT_QLAND: '盘旋后垂直降落',
    AUTOLAND: '自动着陆',
  },
  rover: {
    MANUAL: '手动',
    ACRO: '特技',
    STEERING: '转向',
    HOLD: '保持',
    LOITER: '定点停留',
    FOLLOW: '跟随',
    SIMPLE: '简易',
    DOCK: '停靠',
    CIRCLE: '绕圈',
    AUTO: '自动航线',
    RTL: '返航',
    SMART_RTL: '智能返航',
    GUIDED: '引导',
    INITIALIZING: '初始化中',
  },
  sub: {
    STABILIZE: '自稳',
    ACRO: '特技',
    // 潜水器的 ALT_HOLD 是"定深"，不是飞机上的"定高" —— 直译会误导飞手
    ALT_HOLD: '定深',
    AUTO: '自动航线',
    GUIDED: '引导',
    CIRCLE: '绕圈',
    SURFACE: '上浮',
    POSHOLD: '定点',
    MANUAL: '手动',
    MOTORDETECT: '电机方向检测',
    SURFTRAK: '距底定高',
  },
  tracker: {
    MANUAL: '手动',
    STOP: '停止',
    SCAN: '扫描',
    SERVO_TEST: '舵机测试',
    GUIDED: '引导',
    AUTO: '自动',
    INITIALIZING: '初始化中',
  },
  blimp: {
    LAND: '降落',
    MANUAL: '手动',
    VELOCITY: '速度控制',
    LOITER: '悬停',
    RTL: '返航',
    AUTO: '自动航线',
    HOLD: '保持',
  },
};

/** 一条模式记录。 */
export interface ModeEntry {
  readonly number: number;
  /**
   * 归一化后的短名（去掉枚举前缀），例如 `COPTER_MODE_ALT_HOLD` → `ALT_HOLD`。
   * 这是与上游 `MAV_CMD` / 参数文档对照时使用的名字。
   */
  readonly name: string;
  /**
   * 上游枚举条目全名，例如 `COPTER_MODE_ALT_HOLD`。
   * 手写表（blimp）没有对应的 MAVLink 枚举条目，此时为 `undefined`。
   */
  readonly entryName: string | undefined;
  /** 中文名。没有收录时界面应退回显示 `name`。 */
  readonly label: string | undefined;
}

/** 一个类别的模式表。 */
export interface ModeTable {
  readonly kind: VehicleKind;
  /** 数据来源的枚举名；手写表为 `undefined`。 */
  readonly enumName: string | undefined;
  readonly entries: readonly ModeEntry[];
}

function normalizeModeName(raw: string, enumName: string | undefined): string {
  const upper = raw.trim().toUpperCase();
  if (enumName !== undefined) {
    const prefix = `${enumName.toUpperCase()}_`;
    if (upper.startsWith(prefix)) return upper.slice(prefix.length);
  }
  // 也接受调用方直接传条目全名（`COPTER_MODE_AUTO`）却传错 kind 的情况：
  // 把已知的枚举前缀都剥一遍，比要求调用方先知道 kind 更宽容
  for (const candidate of Object.values(MODE_ENUM_BY_KIND)) {
    if (candidate === undefined) continue;
    const prefix = `${candidate.toUpperCase()}_`;
    if (upper.startsWith(prefix)) return upper.slice(prefix.length);
  }
  return upper;
}

const tableCache = new Map<VehicleKind, ModeTable>();

function buildTable(kind: VehicleKind): ModeTable {
  const enumName = MODE_ENUM_BY_KIND[kind];
  const labels = kind === 'unknown' ? undefined : MODE_LABELS[kind];
  const entries: ModeEntry[] = [];

  if (enumName !== undefined) {
    const enumDef = enumByName(enumName);
    for (const entry of enumDef?.entries ?? []) {
      const name = normalizeModeName(entry.name, enumName);
      entries.push({
        number: entry.value,
        name,
        entryName: entry.name,
        label: labels?.[name],
      });
    }
  } else if (kind === 'blimp') {
    for (const [name, number] of BLIMP_MODES) {
      entries.push({ number, name, entryName: undefined, label: labels?.[name] });
    }
  }

  return { kind, enumName, entries };
}

/** 取某个类别的完整模式表（懒建 + 缓存）。`'unknown'` 类别返回空表。 */
export function modeTable(kind: VehicleKind): ModeTable {
  const cached = tableCache.get(kind);
  if (cached !== undefined) return cached;
  const table = buildTable(kind);
  tableCache.set(kind, table);
  return table;
}

/** 所有已覆盖类别的模式表。 */
export function allModeTables(): readonly ModeTable[] {
  return VEHICLE_KINDS.map((kind) => modeTable(kind));
}

/**
 * 模式号 → 上游短名。未收录的模式号返回 `undefined`
 * （**不要**返回 `'UNKNOWN'` 之类：界面需要区分"真有个叫 UNKNOWN 的模式"
 * 和"这个号我们没见过"，后者应该显示原始数字）。
 */
export function modeName(kind: VehicleKind, modeNumber: number): string | undefined {
  return modeTable(kind).entries.find((entry) => entry.number === modeNumber)?.name;
}

/**
 * 模式名 → 模式号。大小写不敏感，`'auto'`、`'AUTO'`、`'COPTER_MODE_AUTO'` 都能查到。
 * 未收录的模式名返回 `undefined`。
 */
export function modeNumber(kind: VehicleKind, name: string): number | undefined {
  if (name.trim().length === 0) return undefined;
  const normalized = normalizeModeName(name, MODE_ENUM_BY_KIND[kind]);
  return modeTable(kind).entries.find((entry) => entry.name === normalized)?.number;
}

/** 模式号 → 完整记录（含中文名）。 */
export function modeEntry(kind: VehicleKind, modeNumber: number): ModeEntry | undefined {
  return modeTable(kind).entries.find((entry) => entry.number === modeNumber);
}

/**
 * 界面上显示用的一行文字。优先中文名，其次上游名，最后退回 `#编号`。
 *
 * 永远不返回空字符串：模式字段是遥测里最常见的"要显示"的东西，
 * 让调用方每次自己写兜底会漏。
 */
export function modeDisplayName(kind: VehicleKind, modeNumber: number): string {
  const entry = modeEntry(kind, modeNumber);
  if (entry === undefined) return `#${modeNumber}`;
  return entry.label ?? entry.name;
}

/* ────────────────────────── 模式切换门禁 ────────────────────────── */

/** Copter 门禁规则的依据（核对日期 2026-09-25）。 */
export const MODE_SWITCH_RULE_SOURCE =
  'ArduCopter::set_mode()（https://raw.githubusercontent.com/ArduPilot/ardupilot/master/ArduCopter/mode.cpp）' +
  '；requires_position() 与 allows_entry_in_rc_failsafe() 的定义见 ' +
  'https://raw.githubusercontent.com/ArduPilot/ardupilot/master/ArduCopter/mode.h' +
  '（ModeAuto::requires_position 在 mode_auto.cpp，除 NAV_ATTITUDE_TIME 外都要求位置）';

/**
 * 直升机专属门禁（`#if FRAME_CONFIG == HELI_FRAME`）的原文，以及**为什么没有编码它**。
 *
 * 上游 `Copter::set_mode()` 里紧跟 `ignore_checks` 的那段就是它（原文见常量值）。
 * 两点结论，都直接影响 {@link canSwitchMode} 的写法：
 *
 * 1. **它不是"未解锁时的例外"。** 条件里带 `!ignore_checks`，而
 *    `ignore_checks = !motors->armed()`，所以这条只可能在**已解锁**之后拒绝。
 *    未解锁的直升机与别的机架一样走"跳过全部检查"那条路。
 *    换句话说：`ignore_checks` 是**唯一的**未解锁放行开关，找不到第二个反例。
 * 2. **它依赖地面站拿不到的量。** 后半句要 `motors->get_spool_state()` ——
 *    旋翼起转是否已经到 `THROTTLE_UNLIMITED`。这个状态**不在任何遥测消息里**：
 *    ArduPilot 只在拒绝之后才告诉地面站（`mode_change_failed()` 播一条
 *    `runup not complete` 的 STATUSTEXT，命令以失败码收尾）。
 *    若用"已解锁 + 非手动油门模式"来近似它，绝大多数正常情况下
 *    （旋翼早已起转完成）都会被误拦，而用户没有任何办法绕过。
 *
 * 所以这条保持**未编码**：界面按"允许、由飞控最终裁决"处理，
 * 并把飞控回过来的拒绝原文照给用户看。宁可少拦一次，也不要拦错一次。
 *
 * 这段是逐字核对出来的（不是从注释或记忆里转述），核对日期 2026-09-25，
 * 来源见常量值里的 URL。
 */
export const HELI_ROTOR_RUNUP_RULE_SOURCE =
  'ArduCopter/mode.cpp::Copter::set_mode() 的 `#if FRAME_CONFIG == HELI_FRAME` 分支：' +
  '`if (!ignore_checks && !new_flightmode->has_manual_throttle() && ' +
  'motors->get_spool_state() != AP_Motors::SpoolState::THROTTLE_UNLIMITED) { ' +
  'mode_change_failed(new_flightmode, "runup not complete"); return false; }`' +
  '（上游注释：do not allow helis to enter a non-manual throttle mode if the rotor runup ' +
  'is not complete）；核对日期 2026-09-25，来源 ' +
  'https://raw.githubusercontent.com/ArduPilot/ardupilot/master/ArduCopter/mode.cpp';

interface ModeSwitchRules {
  readonly source: string;
  /** 需要位置估计才能进入的模式（上游 `requires_position() === true`）。 */
  readonly requiresPosition: readonly string[];
  /** 在遥控失控（RC failsafe）中禁止进入的模式（上游覆写为 false 的那些）。 */
  readonly blocksRcFailsafeEntry: readonly string[];
}

const MODE_SWITCH_RULES: Readonly<Partial<Record<VehicleKind, ModeSwitchRules>>> = {
  copter: {
    source: MODE_SWITCH_RULE_SOURCE,
    requiresPosition: [
      'AUTO',
      'AUTO_RTL',
      'AVOID_ADSB',
      'BRAKE',
      'CIRCLE',
      'DRIFT',
      'FOLLOW',
      'GUIDED',
      'LOITER',
      'POSHOLD',
      'RTL',
      'SMART_RTL',
      'THROW',
      'ZIGZAG',
    ],
    // Mode 基类默认 allows_entry_in_rc_failsafe() === true，
    // 只有 Acro / Drift / Stabilize / Turtle 覆写为 false
    blocksRcFailsafeEntry: ['ACRO', 'DRIFT', 'STABILIZE', 'TURTLE'],
  },
  // plane / rover / sub / tracker / blimp 的 set_mode 各有各的实现
  // （且不在同一个文件里），没有逐条核对过 → 不编码任何规则，
  // canSwitchMode 对这些类别一律返回"允许"并在 message 里说明。
};

/** 切换模式的判定依据：飞控此刻的状态。 */
export interface ModeSwitchContext {
  /** 当前模式号。`undefined` 表示还不知道（例如刚连上还没收到 HEARTBEAT）。 */
  readonly currentModeNumber: number | undefined;
  /** 电机是否已解锁（对应上游 `motors->armed()`）。 */
  readonly armed: boolean;
  /** 是否有可用的位置估计（对应上游 `copter.position_ok()`）。 */
  readonly hasPositionEstimate: boolean;
  /** 是否处于遥控失控（对应上游 `rc().in_rc_failsafe()`）。 */
  readonly rcFailsafe: boolean;
}

/** 拒绝原因，供界面分类提示（不是自由文本，便于做文案与图标映射）。 */
export type ModeSwitchRejection = 'unknown-mode' | 'requires-position' | 'rc-failsafe';

export interface ModeSwitchVerdict {
  readonly allowed: boolean;
  readonly rejection: ModeSwitchRejection | undefined;
  /** 中文提示，可以直接显示给用户。 */
  readonly message: string;
  /** 判定所依据的规则来源；未编码规则时为 `undefined`。 */
  readonly source: string | undefined;
}

/**
 * 判断"此刻能不能切到目标模式"。
 *
 * 编码的规则（仅 Copter，逐条对应上游 `Copter::set_mode()`）：
 *
 * 1. **目标模式号不存在** → 拒绝。上游 `mode_from_mode_num()` 返回 `nullptr`
 *    时走 `notify_no_such_mode()` 并返回 false。
 * 2. **就是当前模式** → 允许。上游第一步就 `return true`（顺手做了几件
 *    与模式号无关的收尾），这并不是一次真正的切换。
 * 3. **未解锁** → 允许。上游第一句状态检查是
 *    `bool ignore_checks = !motors->armed();` —— 未解锁时**全部**检查被跳过，
 *    "靠解锁检查兜底"。所以"未解锁时能不能进 AUTO"的答案是**能**。
 *    （这一点与直觉相反，也是本函数存在的意义之一：照直觉写的规则会把
 *    正常的解锁流程拦死 —— 用户习惯先切 AUTO 再解锁。）
 *    补充：直升机那段 `#if FRAME_CONFIG == HELI_FRAME` 的"旋翼未起转完成"限制
 *    也带 `!ignore_checks`，因此**未解锁时不生效**，它不是这条规则的例外；
 *    详见 {@link HELI_ROTOR_RUNUP_RULE_SOURCE}。
 * 4. **已解锁 + 目标模式需要位置 + 没有位置估计** → 拒绝（`requires position`）。
 * 5. **已解锁 + 遥控失控 + 目标模式不允许在失控中进入** → 拒绝（`in RC failsafe`）。
 *
 * 上游还有几条本函数**故意没编**的检查，因为它们的输入不在
 * {@link ModeSwitchContext} 里、或者依赖编译期/参数配置：
 * 直升机旋翼转速未达 THROTTLE_UNLIMITED（见
 * {@link HELI_ROTOR_RUNUP_RULE_SOURCE}，它只在已解锁后生效，且需要
 * `motors->get_spool_state()` 这个遥测里没有的量）、
 * 地面手动油门过高时的保护（`FRAME_CONFIG != HELI_FRAME` 分支）、
 * EKF 高度估计缺失（`ekf_alt_ok()`）、
 * 围栏恢复中禁止换模式（`FENCE_OPTIONS` 位）、
 * `FLTMODE_GCSBLOCK` 禁止地面站换模式、`init()` 自身失败。
 * 这些留给飞控去拒绝：本函数不掌握它们的输入，硬编就会变成假阳性拦截。
 *
 * 对未编码规则的类别（plane / rover / sub / tracker / blimp）与未知类别，
 * 一律返回"允许"，并在 `source` 中明确标出"未编码"。
 */
export function canSwitchMode(
  kind: VehicleKind,
  targetModeNumber: number,
  context: ModeSwitchContext,
): ModeSwitchVerdict {
  const entry = modeEntry(kind, targetModeNumber);
  if (entry === undefined) {
    return {
      allowed: false,
      rejection: 'unknown-mode',
      message: `模式号 ${targetModeNumber} 不在 ${kind} 的模式表中，飞控会拒绝这次切换`,
      source: MODE_ENUM_BY_KIND[kind] ?? (kind === 'blimp' ? BLIMP_MODE_SOURCE : undefined),
    };
  }

  if (context.currentModeNumber !== undefined && context.currentModeNumber === targetModeNumber) {
    return {
      allowed: true,
      rejection: undefined,
      message: `已经是「${entry.label ?? entry.name}」模式`,
      source: undefined,
    };
  }

  const rules = MODE_SWITCH_RULES[kind];
  if (rules === undefined) {
    return {
      allowed: true,
      rejection: undefined,
      message: `未编码 ${kind} 的模式门禁规则，按允许处理（真实拒绝由飞控给出）`,
      source: undefined,
    };
  }

  if (!context.armed) {
    return {
      allowed: true,
      rejection: undefined,
      message: '未解锁：飞控会跳过全部换模式检查（ignore_checks）',
      source: rules.source,
    };
  }

  if (rules.requiresPosition.includes(entry.name) && !context.hasPositionEstimate) {
    return {
      allowed: false,
      rejection: 'requires-position',
      message: `「${entry.label ?? entry.name}」需要位置估计，当前没有可用位置`,
      source: rules.source,
    };
  }

  if (context.rcFailsafe && rules.blocksRcFailsafeEntry.includes(entry.name)) {
    return {
      allowed: false,
      rejection: 'rc-failsafe',
      message: `遥控失控中不允许进入「${entry.label ?? entry.name}」`,
      source: rules.source,
    };
  }

  return { allowed: true, rejection: undefined, message: '可以切换', source: rules.source };
}

/**
 * 该模式是否要求位置估计。
 *
 * `undefined` 表示**未编码**（该类别没有规则表，或该模式不在表里），
 * 与 `false` 严格区分：`false` 是"确认不要求"，
 * 调用方不能把"不知道"当成"不要求"。
 */
export function requiresPositionEstimate(kind: VehicleKind, modeNumber: number): boolean | undefined {
  const rules = MODE_SWITCH_RULES[kind];
  if (rules === undefined) return undefined;
  const entry = modeEntry(kind, modeNumber);
  if (entry === undefined) return undefined;
  return rules.requiresPosition.includes(entry.name);
}
